import { composeWrappers } from "@redotech/react-util/component";
import { StyleRoot } from "@redotech/react-util/style";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { RedoClientProvider } from "@redotech/redo-web/client";
import { isLoggedInToStorefront } from "@redotech/redo-web/utils/storefront-is-logged-in";
import { ShopifyAjaxClient } from "@redotech/shopify-client/ajax";
import { memo } from "react";
import { createRoot } from "react-dom/client";
import { ShopifyExtensionClientProvider } from "../client/client-context";
import { RedoShopifyRpcClientContextProvider } from "../client/redo-shopify-rpc-client";
import { ExtensionShopifyClientContext } from "../shopify";
import STYLE_MANAGER from "../style-manager";
import { SignupFormInstance } from "./marketing/signup-form/signup-form-instance";
import { ReviewSurvey } from "./reviews/reviews-survey";
import { SaveForLaterSlideInPanel } from "./save-for-later/save-for-later-slide-in-panel";
import "./styles.css";
import "./styles.global.css";

// also referenced in "./styles.global.css"
const REDO_STOREFRONT_OVERLAY_CONTAINER = "redo-storefront-container";

const RedoStorefrontOverlay = memo(function RedoStorefrontOverlay({
  autocheck,
}: {
  autocheck: WidgetAutocheck;
}) {
  const queryParameters = new URLSearchParams(window.location.search);
  const testForm = queryParameters.get("redo-signup-test");

  const shouldShowSaveForLaterSlideInPanel =
    autocheck.customerAccounts.saveForLaterPanel?.enabled &&
    !isLoggedInToStorefront();
  return composeWrappers(
    (inner) => (
      <RedoClientProvider url={process.env.REDO_API_URL!}>
        {inner}
      </RedoClientProvider>
    ),
    (inner) => (
      <ShopifyExtensionClientProvider
        storeUrl={Shopify?.shop}
        widgetId={window.redoWidgetId}
      >
        {inner}
      </ShopifyExtensionClientProvider>
    ),
    (inner) => (
      <ExtensionShopifyClientContext.Provider
        value={ShopifyAjaxClient.fromEnv()}
      >
        {inner}
      </ExtensionShopifyClientContext.Provider>
    ),
    (inner) => (
      <RedoShopifyRpcClientContextProvider
        baseUrl={process.env.REDO_API_URL!}
        storeUrl={window.Shopify?.shop ?? window.Shopify?.Checkout?.apiHost}
        widgetId={window.redoWidgetId}
      >
        {inner}
      </RedoShopifyRpcClientContextProvider>
    ),
  )(
    <>
      {shouldShowSaveForLaterSlideInPanel && (
        <SaveForLaterSlideInPanel
          saveForLaterPanelSettings={
            autocheck.customerAccounts.saveForLaterPanel
          }
        />
      )}
      {(autocheck.showSignupForm || (autocheck.testSignupForm && testForm)) && (
        <SignupFormInstance brandKit={autocheck.brandKit} />
      )}
      {autocheck.reviews.enabled && <ReviewSurvey />}
    </>,
  );
});

export function initialize(autocheck: WidgetAutocheck) {
  if (window.blockRedo) {
    return;
  }

  if (!document.getElementById(REDO_STOREFRONT_OVERLAY_CONTAINER)) {
    const redoContainer = document.createElement("div");
    redoContainer.id = REDO_STOREFRONT_OVERLAY_CONTAINER;
    document.body.appendChild(redoContainer);

    const root = createRoot(redoContainer);

    root.render(
      <StyleRoot container={redoContainer} styleManager={STYLE_MANAGER}>
        <RedoStorefrontOverlay autocheck={autocheck} />
      </StyleRoot>,
    );
  }
}
